import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@sundkortid/ui';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '';
const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';

type MunicipalityStatus = 'contract' | 'interest' | 'none';

interface Municipality {
  name: string;
  status: MunicipalityStatus;
}

const MUNICIPALITIES: Municipality[] = [
  // Largest first
  { name: 'Reykjavíkurborg', status: 'interest' },
  { name: 'Kópavogsbær', status: 'interest' },
  { name: 'Hafnarfjarðarkaupstaður', status: 'none' },
  { name: 'Akureyrarbær', status: 'none' },
  { name: 'Reykjanesbær', status: 'none' },
  { name: 'Garðabær', status: 'none' },
  { name: 'Mosfellsbær', status: 'none' },
  { name: 'Árborg', status: 'none' },
  { name: 'Akraneskaupstaður', status: 'none' },
  { name: 'Fjarðabyggð', status: 'none' },
  // Shown after "Sjá öll"
  { name: 'Sveitarfélagið Hornafjörður', status: 'none' },
  { name: 'Múlaþing', status: 'none' },
  { name: 'Norðurþing', status: 'none' },
  { name: 'Ísafjarðarbær', status: 'none' },
  { name: 'Vestmannaeyjabær', status: 'none' },
  { name: 'Snæfellsbær', status: 'none' },
  { name: 'Dalvíkurbyggð', status: 'none' },
  { name: 'Fljótsdalshérað', status: 'none' },
  { name: 'Grindavíkurbær', status: 'none' },
  { name: 'Borgarbyggð', status: 'none' },
  { name: 'Hveragerðisbær', status: 'none' },
  { name: 'Sveitarfélagið Ölfus', status: 'none' },
  { name: 'Rangárþing eystra', status: 'none' },
  { name: 'Rangárþing ytra', status: 'none' },
  { name: 'Skagafjörður', status: 'none' },
  { name: 'Blönduósbær', status: 'none' },
  { name: 'Bolungarvíkurkaupstaður', status: 'none' },
  { name: 'Stykkishólmsbær', status: 'none' },
  { name: 'Dalabyggð', status: 'none' },
  { name: 'Húnaþing vestra', status: 'none' },
  { name: 'Sveitarfélagið Skagaströnd', status: 'none' },
  { name: 'Þingeyjarsveit', status: 'none' },
  { name: 'Langanesbyggð', status: 'none' },
  { name: 'Vopnafjarðarhreppur', status: 'none' },
  { name: 'Tálknafjarðarhreppur', status: 'none' },
  { name: 'Reykhólahreppur', status: 'none' },
  { name: 'Strandabyggð', status: 'none' },
  { name: 'Súðavíkurhreppur', status: 'none' },
  { name: 'Sveitarfélagið Vogar', status: 'none' },
  { name: 'Seltjarnarneskaupstaður', status: 'none' },
];

const INITIAL_SHOW = 10;

/* Deterministic QR-like pattern — always renders the same */
const QR_PATTERN = [
  1,1,1,0,1,0,1,1,1,
  1,0,1,1,0,1,1,0,1,
  1,1,1,0,1,0,1,1,1,
  0,0,0,1,1,0,0,0,0,
  1,0,1,0,1,1,0,1,0,
  0,1,0,1,0,0,1,0,1,
  1,1,1,0,0,1,1,1,1,
  1,0,1,0,1,0,1,0,1,
  1,1,1,1,0,1,1,1,1,
];

function AppPreview() {
  return (
    <div className="app-preview">
      <div className="w-[270px] sm:w-[290px]">
        {/* Phone shell */}
        <div className="rounded-[2.2rem] bg-[#1a1a1a] p-[10px] shadow-[0_25px_60px_-10px_rgba(0,50,80,0.25)]">
          {/* Dynamic Island */}
          <div className="absolute top-[10px] left-1/2 -translate-x-1/2 w-[90px] h-[22px] bg-[#1a1a1a] rounded-full z-10" />

          {/* Screen */}
          <div className="rounded-[1.8rem] bg-[#f6f9fb] overflow-hidden">
            {/* Nav bar */}
            <div className="h-14 bg-primary-800 flex items-end px-5 pb-2.5">
              <span className="text-[11px] font-title font-bold text-white/90 tracking-wider uppercase">Sundkortið</span>
            </div>

            {/* Screen content */}
            <div className="px-4 pt-4 pb-6">
              {/* User greeting */}
              <p className="text-[11px] text-gray-400 mb-0.5">Góðan daginn,</p>
              <p className="text-[15px] font-title font-bold text-primary-900 mb-4">Jón Jónsson</p>

              {/* Pass card — mirrors the real PassCard component */}
              <div className="rounded-xl border border-primary-100 bg-white overflow-hidden mb-4">
                <div className="h-[3px] bg-gradient-to-r from-primary via-secondary to-accent" />
                <div className="p-3.5">
                  <div className="flex items-start justify-between mb-1.5">
                    <div>
                      <p className="text-[8px] font-bold tracking-[0.18em] uppercase text-primary-400 mb-1">10 skipta kort</p>
                      <span className="inline-flex items-center gap-1 px-1.5 py-[2px] rounded-full text-[7px] font-semibold bg-green-50 text-green-700">
                        <span className="w-[5px] h-[5px] rounded-full bg-green-500" />
                        Virkt
                      </span>
                    </div>
                    <div className="w-6 h-6 rounded-md bg-primary-50 flex items-center justify-center">
                      <svg className="w-3 h-3 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6.429 9.75 2.25 12l4.179 2.25m0-4.5 5.571 3 5.571-3m-11.142 0L2.25 7.5 12 2.25l9.75 5.25-4.179 2.25m0 0L21.75 12l-4.179 2.25m0 0 4.179 2.25L12 21.75 2.25 16.5l4.179-2.25m11.142 0-5.571 3-5.571-3" />
                      </svg>
                    </div>
                  </div>
                  <div className="flex items-end gap-1 mb-1.5">
                    <span className="text-[18px] font-bold text-primary-900 leading-none">7</span>
                    <span className="text-[9px] text-gray-400 mb-[1px]">af 10 eftir</span>
                  </div>
                  <div className="h-[4px] bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-primary to-secondary" style={{ width: '70%' }} />
                  </div>
                </div>
              </div>

              {/* QR section */}
              <div className="bg-white rounded-xl border border-gray-100 p-3 flex flex-col items-center">
                <div className="w-[100px] h-[100px] grid grid-cols-9 grid-rows-9 gap-[1.5px] mb-1.5">
                  {QR_PATTERN.map((filled, i) => (
                    <div
                      key={i}
                      className={filled ? 'bg-primary-900 rounded-[0.5px]' : ''}
                    />
                  ))}
                </div>
                <p className="text-[8px] text-gray-400 mt-1">Skannaðu til að nota kort</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusIndicator({ status }: { status: MunicipalityStatus }) {
  if (status === 'contract') {
    return (
      <span className="inline-flex items-center gap-1.5 text-green-700 text-xs font-medium">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
        </svg>
        Samningur
      </span>
    );
  }
  if (status === 'interest') {
    return (
      <span className="inline-flex items-center gap-1.5 text-amber-600 text-xs font-medium">
        <span className="w-2 h-2 rounded-full bg-amber-400" />
        Áhugi
      </span>
    );
  }
  return (
    <span className="text-xs text-gray-300">—</span>
  );
}

export function ComingSoonPage() {
  const [email, setEmail] = useState('');
  const [emailToken, setEmailToken] = useState('');
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [emailError, setEmailError] = useState('');
  const emailTurnstileRef = useRef<TurnstileInstance>(null);

  const [supporterName, setSupporterName] = useState('');
  const [anonymous, setAnonymous] = useState(false);
  const [supporterToken, setSupporterToken] = useState('');
  const [supporterSubmitting, setSupporterSubmitting] = useState(false);
  const [supporterSuccess, setSupporterSuccess] = useState(false);
  const [supporterError, setSupporterError] = useState('');
  const supporterTurnstileRef = useRef<TurnstileInstance>(null);

  const [count, setCount] = useState<number | null>(null);
  const [showAllMunicipalities, setShowAllMunicipalities] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/waitlist/supporters/count`)
      .then((r) => r.json())
      .then((json) => setCount(json.data?.count ?? 0))
      .catch(() => {});
  }, []);

  const handleEmailSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError('');
    if (!emailToken) {
      setEmailError('Vinsamlegast kláraðu captcha');
      return;
    }
    setEmailSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, turnstileToken: emailToken }),
      });
      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.error?.message || 'Villa kom upp');
      }
      setEmailSuccess(true);
      setEmail('');
    } catch (err) {
      setEmailError(err instanceof Error ? err.message : 'Villa kom upp');
    } finally {
      setEmailSubmitting(false);
      setEmailToken('');
      emailTurnstileRef.current?.reset();
    }
  }, [email, emailToken]);

  const handleSupporterSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setSupporterError('');
    if (!supporterToken) {
      setSupporterError('Vinsamlegast kláraðu captcha');
      return;
    }
    if (!anonymous && !supporterName.trim()) {
      setSupporterError('Vinsamlegast skráðu nafn eða veldu nafnlaust');
      return;
    }
    setSupporterSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/waitlist/supporters`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: anonymous ? undefined : supporterName.trim(),
          anonymous,
          turnstileToken: supporterToken,
        }),
      });
      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.error?.message || 'Villa kom upp');
      }
      setSupporterSuccess(true);
      setSupporterName('');
      setAnonymous(false);
      setCount((prev) => (prev !== null ? prev + 1 : 1));
    } catch (err) {
      setSupporterError(err instanceof Error ? err.message : 'Villa kom upp');
    } finally {
      setSupporterSubmitting(false);
      setSupporterToken('');
      supporterTurnstileRef.current?.reset();
    }
  }, [anonymous, supporterName, supporterToken]);

  return (
    <div className="min-h-screen bg-white">
      {/* ── Header ── */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 sm:px-8 py-4">
          <Link to="/" className="flex items-center gap-3">
            <img src="/favicon.png" alt="" className="h-9 w-auto" />
            <span className="text-xl font-title font-bold text-primary-800 tracking-wide">
              Sundkortið
            </span>
          </Link>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="relative overflow-hidden border-b border-gray-100">
        {/* Faint watermark */}
        <div className="absolute top-1/2 -translate-y-1/2 right-[-8%] w-[55%] max-w-2xl opacity-[0.04] mask-fade-edges pointer-events-none select-none">
          <img src="/glyphs.png" alt="" className="w-full h-auto" draggable={false} />
        </div>

        <div className="relative max-w-6xl mx-auto px-6 sm:px-8 pt-16 pb-14 md:pt-24 md:pb-20">
          <div className="flex flex-col lg:flex-row items-center lg:items-start gap-14 lg:gap-20">
            {/* Text */}
            <div className="flex-1 max-w-lg lg:pt-8">
              <p className="animate-on-load animate-fade-up text-xs font-semibold tracking-[0.25em] uppercase text-primary-400 mb-5">
                Væntanlegt
              </p>

              <h1 className="animate-on-load animate-fade-up-1 text-4xl sm:text-5xl md:text-6xl font-title font-bold text-primary-900 leading-[1.08] mb-6">
                Eitt kort fyrir allar laugar landsins
              </h1>

              <p className="animate-on-load animate-fade-up-2 text-lg text-gray-500 leading-relaxed mb-3 max-w-md">
                Eitt kort. Allar sundlaugar. Sýndu QR kóðann í símanum í stað þess að leita að sundkortinu á botni töskunnar.
              </p>

              <p className="animate-on-load animate-fade-up-2 text-sm text-gray-400 max-w-sm leading-relaxed">
                Skráðu þig hér að neðan til að fá tilkynningu þegar við opnum.
              </p>
            </div>

            {/* Phone */}
            <div className="animate-on-load animate-fade-up-2 flex-shrink-0 lg:-mr-8">
              <AppPreview />
            </div>
          </div>
        </div>
      </section>

      {/* ── Email signup ── */}
      <section className="py-16 md:py-20 bg-primary-50/30 bg-water-wash">
        <div className="max-w-lg mx-auto px-6 sm:px-8">
          <h2 className="text-2xl md:text-3xl font-title font-bold text-primary-900 mb-2">
            Fáðu tilkynningu
          </h2>
          <p className="text-sm text-gray-400 mb-6">
            Vertu fyrst/ur til að vita þegar Sundkortið opnar.
          </p>

          {emailSuccess ? (
            <div className="rounded-lg border border-green-200 bg-green-50 px-5 py-4 text-green-800 text-sm">
              Takk! Við látum þig vita þegar Sundkortið opnar.
            </div>
          ) : (
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div className="flex gap-3">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Netfangið þitt"
                  className="flex-1 rounded-lg border border-gray-200 bg-white px-4 py-3 text-gray-900 placeholder:text-gray-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200 focus:outline-none transition-colors"
                />
                <Button type="submit" loading={emailSubmitting} className="shrink-0">
                  Skrá mig
                </Button>
              </div>
              <Turnstile
                ref={emailTurnstileRef}
                siteKey={SITE_KEY}
                onSuccess={setEmailToken}
                options={{ theme: 'light', size: 'normal' }}
              />
              {emailError && (
                <p className="text-sm text-red-600">{emailError}</p>
              )}
            </form>
          )}
        </div>
      </section>

      {/* ── Undirskriftalisti ── */}
      <section className="py-16 md:py-20">
        <div className="max-w-lg mx-auto px-6 sm:px-8">
          <h2 className="text-2xl md:text-3xl font-title font-bold text-primary-900 mb-2">
            Undirskriftalisti
          </h2>
          <p className="text-sm text-gray-400 mb-6">
            Sýndu sundkortinu stuðning og áhuga með því að skrá þig á undirskriftarlistann, nafnlaust ef þú vilt.
          </p>

          {count !== null && (
            <div className="mb-6 flex items-baseline gap-3 px-5 py-4 rounded-lg border border-primary-100 bg-primary-50/50">
              <span className="text-3xl font-title font-bold text-primary-700 tabular-nums">{count}</span>
              <span className="text-primary-600 text-sm">
                {count === 1 ? 'hefur skrifað undir' : 'hafa skrifað undir'}
              </span>
            </div>
          )}

          {supporterSuccess ? (
            <div className="rounded-lg border border-green-200 bg-green-50 px-5 py-4 text-green-800 text-sm">
              Takk fyrir stuðninginn!
            </div>
          ) : (
            <form onSubmit={handleSupporterSubmit} className="space-y-4">
              <input
                type="text"
                value={supporterName}
                onChange={(e) => setSupporterName(e.target.value)}
                placeholder="Nafnið þitt"
                disabled={anonymous}
                className="w-full rounded-lg border border-gray-200 bg-white px-4 py-3 text-gray-900 placeholder:text-gray-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200 focus:outline-none transition-colors disabled:bg-gray-50 disabled:text-gray-400"
              />
              <label className="flex items-center gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={anonymous}
                  onChange={(e) => setAnonymous(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary-300"
                />
                <span className="text-sm text-gray-600">Ég vil vera nafnlaus</span>
              </label>
              <Turnstile
                ref={supporterTurnstileRef}
                siteKey={SITE_KEY}
                onSuccess={setSupporterToken}
                options={{ theme: 'light', size: 'normal' }}
              />
              {supporterError && (
                <p className="text-sm text-red-600">{supporterError}</p>
              )}
              <Button type="submit" loading={supporterSubmitting} variant="outline" className="w-full">
                Skrifa undir
              </Button>
            </form>
          )}
        </div>
      </section>

      {/* ── Sveitarfélög ── */}
      <section className="py-16 md:py-20 bg-primary-50/30 bg-water-wash">
        <div className="max-w-lg mx-auto px-6 sm:px-8">
          <h2 className="text-2xl md:text-3xl font-title font-bold text-primary-900 mb-2">
            Sveitarfélög
          </h2>
          <p className="text-sm text-gray-400 mb-6">
            Sveitarfélög sem styðja sundkortið.
          </p>

          <div className="flex items-center gap-5 mb-4 text-xs text-gray-500">
            <span className="flex items-center gap-1.5">
              <span className="text-gray-300">—</span>
              Ekkert enn
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-amber-400" />
              Áhugi
            </span>
            <span className="flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
              Í samstarfi
            </span>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
            {/* Header */}
            <div className="flex items-center px-4 py-2.5 bg-gray-50 border-b border-gray-200 text-[11px] font-semibold tracking-wide uppercase text-gray-400">
              <span className="flex-1">Sveitarfélag</span>
              <span className="w-20 text-center">Áhugi</span>
              <span className="w-20 text-center">Í samstarfi</span>
            </div>
            <div className="divide-y divide-gray-100">
              {(showAllMunicipalities ? MUNICIPALITIES : MUNICIPALITIES.slice(0, INITIAL_SHOW)).map((m) => (
                <div key={m.name} className="flex items-center px-4 py-3">
                  <span className="flex-1 text-sm text-gray-700">{m.name}</span>
                  <span className="w-20 flex justify-center">
                    {m.status === 'interest' || m.status === 'contract' ? (
                      <span className="w-2 h-2 rounded-full bg-amber-400" />
                    ) : (
                      <span className="text-gray-200">—</span>
                    )}
                  </span>
                  <span className="w-20 flex justify-center">
                    {m.status === 'contract' ? (
                      <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                    ) : (
                      <span className="text-gray-200">—</span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {!showAllMunicipalities && MUNICIPALITIES.length > INITIAL_SHOW && (
            <button
              type="button"
              onClick={() => setShowAllMunicipalities(true)}
              className="mt-4 w-full text-sm font-medium text-primary-600 hover:text-primary-800 transition-colors py-2"
            >
              Sjá öll ({MUNICIPALITIES.length})
            </button>
          )}
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-gray-50 border-t border-gray-100 py-12">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/favicon.png" alt="" className="h-6 w-auto opacity-30" />
            <span className="text-sm text-gray-400">
              &copy; {new Date().getFullYear()} Sundkortið
            </span>
          </div>
          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-400">
            <Link to="/skilmalar" className="hover:text-gray-600 transition-colors">Skilmálar</Link>
            <Link to="/personuvernd" className="hover:text-gray-600 transition-colors">Persónuvernd</Link>
            <a href="mailto:info@sundkortid.is" className="hover:text-gray-600 transition-colors">info@sundkortid.is</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
